import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, UserPlus, CheckCircle2, FileSpreadsheet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportContacts({ token, onAuthError }) {
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [tag, setTag] = useState("Up First Lead");
  const [imported, setImported] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true); setResult(null); setImported(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/imports/parse-contacts`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Found ${data.contacts.length} contacts (${data.skipped} skipped)`);
    } catch (err) {
      if (err?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error(err?.response?.data?.detail || "Could not read the file");
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const doImport = async () => {
    if (!result?.contacts?.length) return;
    if (!window.confirm(`Import ${result.contacts.length} contacts into GoHighLevel with tag "${tag}"?`)) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/imports/ghl-contacts`,
        { contacts: result.contacts, tag, source: "Up First Import" }, auth);
      setImported(data);
      toast.success(`Imported ${data.imported} contacts to your CRM`);
    } catch (err) {
      if (err?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-contacts">
      <div>
        <h2 className="text-xl font-bold text-white">Import Contacts to CRM</h2>
        <p className="text-white/50 text-sm">Upload an Up First call export (or any CSV/Excel with name + phone columns) and push the contacts into GoHighLevel.</p>
      </div>

      <label data-testid="import-file-input" className="flex flex-col items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-white/15 hover:border-[#F2B705]/50 rounded-xl py-10 text-white/60 transition-colors">
        {parsing ? <Loader2 className="animate-spin" size={28} /> : <Upload size={28} />}
        <span className="font-semibold">{parsing ? "Reading file…" : "Upload CSV or Excel (.csv, .xlsx)"}</span>
        <span className="text-xs text-white/35">We auto-detect the name &amp; phone columns and skip toll-free / duplicate numbers</span>
        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} disabled={parsing} />
      </label>

      {result && (
        <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5 text-white/70"><FileSpreadsheet size={15} className="text-[#F2B705]" /> {result.total_rows} rows</span>
            <span className="text-green-400 font-semibold">{result.contacts.length} importable</span>
            <span className="text-white/40">{result.skipped} skipped</span>
            <span className="text-white/40 text-xs">cols: {result.detected.name_column || "—"} / {result.detected.phone_column}</span>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-white/50 mb-1">CRM Tag</label>
              <input data-testid="import-tag-input" value={tag} onChange={(e) => setTag(e.target.value)}
                className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none" />
            </div>
            <button data-testid="import-submit-btn" onClick={doImport} disabled={importing || !result.contacts.length}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
              {importing ? <Loader2 className="animate-spin" size={15} /> : <UserPlus size={15} />} Import {result.contacts.length} to CRM
            </button>
          </div>

          {imported && (
            <div data-testid="import-result" className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-sm text-green-300">
              <CheckCircle2 size={18} /> Imported <b>{imported.imported}</b> · skipped {imported.skipped} · failed {imported.failed}
              {imported.errors?.length ? <span className="text-red-300/70 ml-2">({imported.errors[0]})</span> : null}
            </div>
          )}

          <div className="max-h-72 overflow-auto divide-y divide-white/5">
            {result.contacts.slice(0, 200).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="text-white/80">{c.name || <span className="text-white/35 italic">No name</span>}</span>
                <span className="text-white/50">{c.phone}</span>
              </div>
            ))}
            {result.contacts.length > 200 && <p className="text-white/35 text-xs py-2">+ {result.contacts.length - 200} more…</p>}
          </div>
        </div>
      )}
    </div>
  );
}
